import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { clearFiltersStore } from 'src/app/data/store/actions/filters.actions';
import { selectCurrentPlatform } from 'src/app/data/store/selectors/platform.selectors';
import { selectUnreadCommentsCounter } from 'src/app/data/store/selectors/users.selectors';
import {
  befreeUrlValues,
  footerMenuOptions,
  userAgreementPendingStorageKey,
} from 'src/app/shared/constants';
import { CurrentPlatform, ScrollToType, TextSizes, TextType } from 'src/app/shared/enums';
import { PushService } from 'src/app/shared/services/push/push.service';
import { ScrollToService } from 'src/app/shared/services/scroll-to/scroll-to.service';
import { ScrollPositionSaver } from '../../shared/services/scroll-position-saver/scroll-position-saver';
import { initPostsData } from '../../data/store/actions/posts.actions';
import { getCounters } from 'src/app/data/store/actions/users.actions';

import { HeaderComponent } from './components/header/header.component';
import { NotificationsCounterComponent } from '../../shared/components/notifications-counter/notifications-counter.component';
import { NotificationContainerComponent } from '../../shared/components/notification-container/notification-container.component';
import { SvgComponent } from 'src/app/shared/components/svg/svg.component';
import { UserAgreementModalComponent } from 'src/app/shared/components/user-agreement-modal/user-agreement-modal.component';

@UntilDestroy()
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    AsyncPipe,
    SvgComponent,
    HeaderComponent,
    RouterOutlet,
    NotificationsCounterComponent,
    NotificationContainerComponent,
    UserAgreementModalComponent,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  router: Router = inject(Router);
  private _store = inject(Store);
  private _pushService = inject(PushService);
  private _scrollToService = inject(ScrollToService);
  private _scrollPositionSaver = inject(ScrollPositionSaver);

  @ViewChild('footer') footer?: ElementRef<HTMLDivElement>;

  public readonly TextSizes = TextSizes;

  public readonly TextType = TextType;

  public readonly befreeUrlValues = befreeUrlValues;

  public readonly footerMenuOptions = footerMenuOptions;

  public readonly CurrentPlatform = CurrentPlatform;

  public unreadComments$: Observable<number | undefined> = this._store.select(
    selectUnreadCommentsCounter,
  );

  public platform$: Observable<CurrentPlatform | null> = this._store.select(selectCurrentPlatform);

  public currentUrl = this.router.url.replace('/dashboard/', '');

  public isFooterShown = true;

  public isHeaderShown = true;

  public isUserAgreementPending = false;

  private _routerSubscription?: Subscription;

  private static readonly NOT_TO_SHOW_FOOTER = [
    /dashboard\/posts\//,
    new RegExp(`/${befreeUrlValues.dashboard}/${befreeUrlValues.avatar}`),
  ];

  private static readonly NOT_TO_SHOW_HEADER = [
    new RegExp(`/${befreeUrlValues.dashboard}/${befreeUrlValues.avatar}`),
  ];

  constructor() {
    this._updateUrlFlags(this.router.url);
    this._routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const currentPath = this.router.url.split('?')[0];
        const nextPath = event.url.split('?')[0];

        if (currentPath !== nextPath) {
          this._store.dispatch(clearFiltersStore());
        }
      }
      if (event instanceof NavigationEnd) {
        this._updateUrlFlags(event.urlAfterRedirects ?? event.url);
      }
    });
  }

  private _updateUrlFlags(url: string): void {
    this.isFooterShown = !DashboardComponent.NOT_TO_SHOW_FOOTER.some((re) => re.test(url));
    this.isHeaderShown = !DashboardComponent.NOT_TO_SHOW_HEADER.some((re) => re.test(url));
  }

  ngOnInit(): void {
    this._store.dispatch(getCounters());
    this._pushService.initWebPush();
    this.isUserAgreementPending = localStorage.getItem(userAgreementPendingStorageKey) === '1';
  }

  ngOnDestroy(): void {
    this._routerSubscription?.unsubscribe();
  }

  public navigate(url: string) {
    this._scrollPositionSaver.deletePositions();
    sessionStorage.clear();

    if (url === befreeUrlValues.posts) {
      this._store.dispatch(initPostsData());
    }

    if (this.currentUrl === url) {
      this._scrollToService.scrollToEvent({ scrollTo: ScrollToType.top });
    }

    this.router.navigate([`${befreeUrlValues.dashboard}/${url}`]);
    this.currentUrl = url;
  }

  public trackByFn(index: number) {
    return index;
  }

  public onUserAgreementAccepted(): void {
    localStorage.removeItem(userAgreementPendingStorageKey);
    this.isUserAgreementPending = false;
  }
}
